<template>
    <div class="responsible-table">
        <TableScrollHeader
            :can-scroll-left="canScrollLeft"
            :can-scroll-right="canScrollRight"
            :scroll-percent="scrollPercent"
            :sticky-offset="stickyOffset"
            @scroll-left="scrollHorizontally(-1)"
            @scroll-right="scrollHorizontally(1)"
        />

        <div class="table-scroll-container" ref="tableScroll" @scroll="updateScrollState">
            <table class="table table-striped table-hover align-middle">
                <thead>
                    <tr>
                        <th v-if="enableSelection" class="select-header">
                            <div class="form-check">
                                <input
                                    class="form-check-input"
                                    type="checkbox"
                                    :checked="selectAll"
                                    @change="toggleSelectAll"
                                />
                            </div>
                        </th>
                        <th
                            v-for="header in normalizedHeaders"
                            :key="header.key"
                            class="sortable-header"
                            role="button"
                            tabindex="0"
                            @click="sortBy(header.key)"
                            @keydown.enter.prevent="sortBy(header.key)"
                        >
                            <span>{{ header.label }}</span>
                            <span v-if="sortColumn === header.key" class="sort-indicator">
                                <font-awesome-icon
                                    v-if="sortDirection !== 'asc'"
                                    :icon="['fas', 'chevron-up']"
                                />
                                <font-awesome-icon
                                    v-if="sortDirection !== 'desc'"
                                    :icon="['fas', 'chevron-down']"
                                />
                            </span>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(row, index) in paginatedData" :key="rowKeyValue(row, index)">
                        <td v-if="enableSelection" class="select-cell">
                            <div class="form-check">
                                <input
                                    class="form-check-input"
                                    type="checkbox"
                                    :value="rowKeyValue(row, index)"
                                    v-model="selectedIds"
                                    @change="emitSelection"
                                />
                            </div>
                        </td>
                        <td v-for="header in normalizedHeaders" :key="`${header.key}-${index}`">
                            {{ formatCell(row, header) }}
                        </td>
                    </tr>
                    <tr v-if="paginatedData.length === 0">
                        <td :colspan="normalizedHeaders.length + (enableSelection ? 1 : 0)">
                            <div class="text-center text-muted py-3">No data available</div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div v-if="totalPages > 1" class="pagination-controls">
            <div v-if="showControls" class="table-controls-inline">
                <label class="form-label" for="itemsPerPage">Items</label>
                <select
                    id="itemsPerPage"
                    class="form-select form-select-sm"
                    v-model.number="itemsPerPage"
                    @change="handleItemsPerPageChange"
                >
                    <option v-for="option in itemsPerPageOptions" :key="option" :value="option">
                        {{ option }}
                    </option>
                </select>
            </div>
            <div class="pagination-actions">
                <button
                    class="btn btn-sm btn-outline-secondary"
                    :disabled="currentPage === 1"
                    @click="changePage(currentPage - 1)"
                >
                    Prev
                </button>
                <span class="page-indicator">Page {{ currentPage }} of {{ totalPages }}</span>
                <button
                    class="btn btn-sm btn-outline-secondary"
                    :disabled="currentPage === totalPages"
                    @click="changePage(currentPage + 1)"
                >
                    Next
                </button>
            </div>
        </div>
    </div>
</template>

<script>
    import TableScrollHeader from '@/components/shared/TableScrollHeader.vue'

    export default {
        name: 'SimpleDataTable',
        components: {
            TableScrollHeader,
        },
        props: {
            headers: {
                type: [Array, Object],
                required: true,
            },
            data: {
                type: Array,
                required: true,
            },
            rowKey: {
                type: String,
                default: 'id',
            },
            itemsPerPageOptions: {
                type: Array,
                default: () => [5, 10, 20, 50],
            },
            defaultItemsPerPage: {
                type: Number,
                default: 5,
            },
            enableSelection: {
                type: Boolean,
                default: true,
            },
            showControls: {
                type: Boolean,
                default: true,
            },
            stickyOffset: {
                type: Number,
                default: 70,
            },
        },
        data() {
            return {
                sortColumn: null,
                sortDirection: 'asc',
                currentPage: 1,
                itemsPerPage: this.defaultItemsPerPage,
                selectedIds: [],
                canScrollLeft: false,
                canScrollRight: false,
                scrollPercent: 0,
            }
        },
        computed: {
            normalizedHeaders() {
                if (Array.isArray(this.headers)) {
                    return this.headers.map((header) => ({
                        key: header.key || header.field || header.name,
                        label: header.label || header.name || header.key || '',
                        formatter: header.formatter || header.formatFn,
                    }))
                }

                return Object.entries(this.headers || {}).map(([key, label]) => ({
                    key,
                    label: typeof label === 'string' ? label : label?.label || key,
                    formatter: label?.formatter || label?.formatFn,
                }))
            },
            selectAll: {
                get() {
                    return (
                        this.enableSelection &&
                        this.selectedIds.length > 0 &&
                        this.selectedIds.length === this.data.length
                    )
                },
                set(value) {
                    if (!this.enableSelection) return
                    if (value) {
                        this.selectedIds = this.data.map((item, index) =>
                            this.rowKeyValue(item, index)
                        )
                    } else {
                        this.selectedIds = []
                    }
                    this.emitSelection()
                },
            },
            sortedData() {
                if (!this.sortColumn) return this.data
                return this.data.slice().sort((a, b) => {
                    const valueA = a[this.sortColumn]
                    const valueB = b[this.sortColumn]
                    if (typeof valueA === 'number' && typeof valueB === 'number') {
                        return this.sortDirection === 'asc' ? valueA - valueB : valueB - valueA
                    }
                    return this.sortDirection === 'asc'
                        ? String(valueA ?? '').localeCompare(String(valueB ?? ''))
                        : String(valueB ?? '').localeCompare(String(valueA ?? ''))
                })
            },
            paginatedData() {
                const start = (this.currentPage - 1) * this.itemsPerPage
                return this.sortedData.slice(start, start + this.itemsPerPage)
            },
            totalPages() {
                return Math.max(1, Math.ceil(this.data.length / this.itemsPerPage))
            },
        },
        watch: {
            data() {
                const currentIds = new Set(
                    this.data.map((item, index) => this.rowKeyValue(item, index))
                )
                this.selectedIds = this.selectedIds.filter((id) => currentIds.has(id))
                this.emitSelection()
                if (this.currentPage > this.totalPages) {
                    this.currentPage = this.totalPages
                }
                this.$nextTick(this.updateScrollState)
            },
        },
        mounted() {
            this.$nextTick(this.updateScrollState)
        },
        methods: {
            rowKeyValue(row, index) {
                return row?.[this.rowKey] ?? row?.id ?? index
            },
            sortBy(column) {
                if (this.sortColumn === column) {
                    this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
                } else {
                    this.sortColumn = column
                    this.sortDirection = 'asc'
                }
            },
            changePage(page) {
                if (page > 0 && page <= this.totalPages) {
                    this.currentPage = page
                }
            },
            handleItemsPerPageChange() {
                this.currentPage = 1
            },
            toggleSelectAll() {
                this.selectAll = !this.selectAll
            },
            emitSelection() {
                this.$emit('selection-changed', this.selectedIds)
            },
            clearSelection() {
                this.selectedIds = []
                this.emitSelection()
            },
            scrollHorizontally(direction = 1) {
                const container = this.$refs.tableScroll
                if (!container) return
                const amount = container.clientWidth * 0.6 * direction
                container.scrollBy({ left: amount, behavior: 'smooth' })
            },
            updateScrollState() {
                const container = this.$refs.tableScroll
                if (!container) return
                const { scrollLeft, scrollWidth, clientWidth } = container
                const maxScroll = Math.max(scrollWidth - clientWidth, 0)
                this.canScrollLeft = scrollLeft > 0
                this.canScrollRight = scrollLeft < maxScroll - 1
                this.scrollPercent =
                    maxScroll > 0 ? Math.min((scrollLeft / maxScroll) * 100, 100) : 0
            },
            formatCell(row, header) {
                const value = row?.[header.key]
                if (typeof header.formatter === 'function') {
                    try {
                        return header.formatter(value, row)
                    } catch (error) {
                        console.error('Failed to format cell', error)
                    }
                }
                if (value === null || value === undefined || value === '') {
                    return ''
                }
                return value
            },
        },
    }
</script>

<style scoped>
    .responsible-table {
        width: 100%;
    }
    .sortable-header {
        user-select: none;
        white-space: nowrap;
    }
    .table-scroll-container {
        overflow-x: auto;
        width: 100%;
    }
    .sort-indicator {
        margin-left: 6px;
        color: #6c757d;
    }
    .select-header,
    .select-cell {
        width: 50px;
    }
    .pagination-controls {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 0.75rem;
        margin-top: 0.5rem;
    }
    .page-indicator {
        font-size: 0.9rem;
        color: #495057;
    }
    .pagination-actions {
        display: inline-flex;
        align-items: center;
        gap: 0.75rem;
    }
    .table-controls-inline {
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
    }
</style>
